import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { useId } from 'react';
import { SectionHeader } from './SectionHeader.tsx';
import { shadow } from '../../styles/tokens/semantic.js';
import type { ActionDescriptor } from '@finance/contracts';

export type SectionVariant = 'plain' | 'surface' | 'subtle' | 'bordered' | 'elevated';
export type SectionPadding = 'none' | 'standard';

export interface PageSectionProps {
  id?: string;
  title?: string;
  description?: string;
  actions?: ActionDescriptor[];
  variant?: SectionVariant;
  padding?: SectionPadding;
  onAction?: (action: ActionDescriptor) => void;
  children: React.ReactNode;
}

export function PageSection({
  id,
  title,
  description,
  actions,
  variant = 'surface',
  padding = 'standard',
  onAction,
  children,
}: PageSectionProps) {
  const theme = useTheme();
  const generatedTitleId = useId();
  const titleId = title ? generatedTitleId : undefined;
  const gap = theme?.financeLayout?.page?.sectionGap?.comfortable ?? 3;

  const variantSx: Record<SectionVariant, object> = {
    plain: { bgcolor: 'transparent', border: 0, boxShadow: 'none' },
    surface: { bgcolor: 'finance.surface.raised', border: 1, borderColor: 'finance.border.medium' },
    subtle: { bgcolor: 'finance.surface.subtle', border: 1, borderColor: 'finance.border.subtle' },
    bordered: { bgcolor: 'finance.surface.raised', border: 1, borderColor: 'finance.border.strong' },
    elevated: {
      bgcolor: 'finance.surface.raised',
      border: 1,
      borderColor: 'finance.border.subtle',
      boxShadow: shadow.surface,
    },
  };

  const isPlain = variant === 'plain';
  const paddingValue = isPlain ? 0 : padding === 'none' ? 0 : theme?.financeLayout?.cardLayout?.padding?.standard ?? 2;

  return (
    <Box
      id={id}
      component="section"
      className="page-section"
      aria-labelledby={titleId}
      sx={{
        borderRadius: theme?.financeLayout?.cardLayout?.borderRadius ?? 2,
        mb: gap,
        overflow: padding === 'none' && !isPlain ? 'hidden' : 'visible',
        p: paddingValue,
        ...variantSx[variant],
      }}
    >
      <SectionHeader id={titleId} title={title} description={description} actions={actions} onAction={onAction} />
      {children}
    </Box>
  );
}
